/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockViewProps
} from '@msdyn365-commerce-modules/content-block';
import { IImageSettings } from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IContentBlockConfig } from '../definition-extensions/content-block.ext.props.autogenerated';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IContentBlockViewProps> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent
    } = props;
    const config = props.config as IContentBlockConfig;

    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;
    const imageClass: string = hasImage ? `${detailsContainer.className} withImage` : `${detailsContainer.className} withoutImage`;
    let newImage: any;

    // Disables custom image size based on viewport and margins added directly to images
    if (config.disableRemoteImageHandling && image) {
        const imageSettings: IImageSettings = { ...(image as any).props.imageSettings };

        if (imageSettings?.viewports) {
            Object.keys(imageSettings.viewports).forEach((key: string) => {
                if (imageSettings.viewports[key]?.q) {
                    imageSettings.viewports[key].q = 'm=0';
                }
            });
    
            newImage = React.cloneElement(image as React.ReactElement, { imageSettings });
            imageContainer.className = `${imageContainer.className} disable-rih`;
        }
    }

    let offsetStyling: React.CSSProperties | undefined = undefined;

    // Regex to check if string starts with a number, followed by either 'px' or '%' and then a comma and the same pattern again. Numbers can also be negative.
    const offsetRegex = /^-?\d+(px|%)(,-?\d+(px|%))*$/;
    if (config.offsetLinks && offsetRegex.test(config.offsetLinks)) {
        offsetStyling = {
            transform: `translate(${config.offsetLinks})`
        };
    }

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{newImage || image}</Node>
                </Node>
                <Node {...detailsContainer} style={offsetStyling}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer}>{newImage || image}</Node>
            <Node className={imageClass} style={offsetStyling}>
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default contentBlockView;
